<script setup lang="ts">
type DataRequestData = App.DataRequests.Data.DataRequestData;
type EnumData = App.Base.Data.EnumData;
type FinancialAidData = App.Students.Data.FinancialAidData;
type PartialFinancialAidData = App.Students.Data.PartialFinancialAidData;
type StudentData = App.Students.Data.StudentData;
import type { Selectable } from '@/components/selectBox/selectBox';

import { formatDate } from '@/utils/date';

import DataRequestSlideout from '@/domains/data-request/components/adviser/DataRequestSlideout.vue';
import DataRequestSlideoutConfirmationDialog from '@/domains/data-request/components/adviser/DataRequestSlideoutConfirmationDialog.vue';
import FinancialAidFormFields from '@/components/students/studentActions/FinancialAidFormFields.vue';

export type ShowDataRequestProps = {
  dataRequest: DataRequestData;
  financialAid: FinancialAidData;
  studentCssProfiles: EnumData[];
  statuses: EnumData[];
  selectedForVerificationOptions: EnumData[];
};

const props = defineProps<ShowDataRequestProps>();

const [isRejectDialogOpen, rejectDialogToggle] = useToggle(false);

const dataRequestAttributes = computed(
  () => props.dataRequest.attributes as PartialFinancialAidData
);

const isPending = computed(() => props.dataRequest.status.value === 'pending');

const form = useForm({
  method: 'PUT',
  url: route('students.financial-aid.update', {
    financialAid: props.dataRequest.requestable?.id,
    student: props.dataRequest.user?.id
  }),
  fields: {
    status:
      ((dataRequestAttributes.value?.status ||
        props.financialAid?.status) as Selectable<string> | null) ?? null,
    date_submitted: formatDate(
      dataRequestAttributes.value?.date_submitted ?? props.financialAid?.date_submitted,
      { format: 'yyyy-MM-dd' }
    ),
    css_profile:
      (dataRequestAttributes.value?.css_profile || props.financialAid?.css_profile) ?? null,
    selected_for_verification:
      (dataRequestAttributes.value?.selected_for_verification ||
        props.financialAid?.selected_for_verification) ??
      false,
    pell_eligible: {
      label: getBooleanDisplayValue<string>(
        dataRequestAttributes.value?.pell_eligible ?? props.financialAid?.pell_eligible,
        'Not set',
        'Eligible',
        'Not Eligible'
      ),
      value: dataRequestAttributes.value?.pell_eligible ?? props.financialAid?.pell_eligible
    },
    is_alternative: props.financialAid?.is_alternative
  },
  transform: (fields) => ({
    ...fields,
    data_request_id: props.dataRequest.id,
    return_to_route: 'data-requests.index',
    status: fields.status?.value,
    css_profile: fields.css_profile?.value,
    selected_for_verification: fields.selected_for_verification?.value,
    pell_eligible: fields.pell_eligible?.value
  })
});

const rejectForm = useForm({
  method: 'POST',
  url: route('data-requests.reject', {
    dataRequest: props.dataRequest.id
  }),
  fields: {
    rejected_reason: ''
  },
  hooks: {
    success() {
      rejectDialogToggle();
    }
  }
});

function handleRejectDataRequest() {
  rejectDialogToggle();
}

function handleRejectDataRequestConfirm() {
  rejectForm.submit();
}
</script>

<template>
  <DataRequestSlideout
    :dataRequest="dataRequest"
    :hasErrors="form.hasErrors"
    :isApproveLoading="form.processing"
    :isDirty="form.isDirty"
    @approveDataRequest="form.submit"
    @rejectDataRequest="handleRejectDataRequest"
  >
    <FinancialAidFormFields
      :financialAid="props.financialAid"
      :form="form"
      :isReadonly="!isPending"
      :selectedForVerificationOptions="selectedForVerificationOptions"
      :statuses="statuses"
      :student="dataRequest.user as StudentData"
      :studentCssProfiles="studentCssProfiles"
    />
  </DataRequestSlideout>
  <DataRequestSlideoutConfirmationDialog
    :form="rejectForm"
    :isOpen="isRejectDialogOpen"
    @onConfirm="() => handleRejectDataRequestConfirm()"
    @onCancel="() => rejectDialogToggle()"
    @onClose="() => rejectDialogToggle()"
  />
</template>
